import { useState, useEffect, FormEvent } from 'react';
import { AdminService, CreateServiceRequest } from '../../../types/admin.types';
import { FormInput } from './form/FormInput';
import { FormSelect } from './form/FormSelect';
import { FormTextarea } from './form/FormTextarea';
import {
  CATEGORY_OPTIONS,
  INITIAL_FORM_DATA,
  FormErrors,
  generateSlug,
  validateServiceForm,
  initializeFormData,
} from './form/serviceFormUtils';

interface ServiceFormProps {
  initialData?: AdminService;
  isSubmitting: boolean;
  onSubmit: (data: CreateServiceRequest) => Promise<void>;
  onCancel: () => void;
}

export function ServiceForm({
  initialData,
  isSubmitting,
  onSubmit,
  onCancel,
}: ServiceFormProps) {
  const isEditMode = Boolean(initialData);
  const [formData, setFormData] = useState<CreateServiceRequest>(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (initialData) {
      setFormData(initializeFormData(initialData));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let parsedValue: string | number | undefined = value;

    if (type === 'number') {
      parsedValue = value === '' ? undefined : Number(value);
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: parsedValue };
      if (name === 'name' && !isEditMode) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });

    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errors = validateServiceForm(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormInput
          id="name"
          name="name"
          label="Name"
          value={formData.name}
          onChange={handleChange}
          error={formErrors.name}
          required
        />

        <FormInput
          id="slug"
          name="slug"
          label="Slug"
          value={formData.slug}
          onChange={handleChange}
          error={formErrors.slug}
          required
        />

        <FormSelect
          id="category"
          name="category"
          label="Category"
          value={formData.category}
          onChange={handleChange}
          options={[...CATEGORY_OPTIONS]}
          error={formErrors.category}
          required
          placeholder="Select a category"
        />

        <FormInput
          id="requiredQualification"
          name="requiredQualification"
          label="Required Qualification"
          value={formData.requiredQualification}
          onChange={handleChange}
          error={formErrors.requiredQualification}
          required
        />

        <FormInput
          id="duration"
          name="duration"
          label="Duration (minutes)"
          type="number"
          value={formData.duration}
          onChange={handleChange}
          error={formErrors.duration}
          min={1}
          required
        />

        <FormInput
          id="basePrice"
          name="basePrice"
          label="Base Price"
          type="number"
          value={formData.basePrice}
          onChange={handleChange}
          error={formErrors.basePrice}
          min={0}
          step="0.01"
          required
        />

        <FormInput
          id="depositAmount"
          name="depositAmount"
          label="Deposit Amount"
          type="number"
          value={formData.depositAmount}
          onChange={handleChange}
          min={0}
          step="0.01"
        />

        <FormInput
          id="displayOrder"
          name="displayOrder"
          label="Display Order"
          type="number"
          value={formData.displayOrder}
          onChange={handleChange}
          min={0}
        />

        <FormInput
          id="imageUrl"
          name="imageUrl"
          label="Image URL"
          value={formData.imageUrl}
          onChange={handleChange}
          className="md:col-span-2"
        />

        <FormTextarea
          id="description"
          name="description"
          label="Description"
          value={formData.description}
          onChange={handleChange}
          error={formErrors.description}
          required
          rows={4}
          className="md:col-span-2"
        />
      </div>

      <div className="flex justify-end gap-3 border-t pt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md border border-transparent bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : isEditMode ? 'Update Service' : 'Create Service'}
        </button>
      </div>
    </form>
  );
}
